import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GlowButton } from "@/components/ui/glow-button";
import { useToast } from "@/hooks/use-toast";

const isRecoveryInUrl = () => {
  try {
    const search = window.location.search;
    const hash = window.location.hash;
    const paramsFromHash = new URLSearchParams(hash.replace('#', '?'));
    const paramsFromSearch = new URLSearchParams(search);
    return (
      paramsFromHash.get('type') === 'recovery' ||
      paramsFromSearch.get('type') === 'recovery'
    );
  } catch (_) {
    return false;
  }
};

export default function PasswordResetHandler() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized initial flag from URL so modal can open immediately on load
  const urlIndicatesRecovery = useMemo(() => isRecoveryInUrl(), []);

  useEffect(() => {
    // Open immediately if URL indicates recovery
    if (urlIndicatesRecovery) setOpen(true);

    // Listen for Supabase recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setOpen(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [urlIndicatesRecovery]);

  const handleUpdate = async () => {
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      setError(error.message);
      return;
    }

    // Clean URL hash/query from recovery tokens
    try {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    } catch { /* no-op */ }

    setOpen(false);
    setPassword("");
    setConfirm("");
    toast({
      title: "Password updated",
      description: "Your password has been changed successfully. Please sign in again.",
    });

    // Optional: sign out to enforce fresh login
    await supabase.auth.signOut();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set a new password</DialogTitle>
          <DialogDescription>
            Enter and confirm your new password to complete the reset.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter a strong password"
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <GlowButton
            label={loading ? "Updating..." : "Update password"}
            onClick={handleUpdate}
            disabled={loading}
            variant="primary"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
